import { lazy, useMemo } from "react";
import { useIntl } from "react-intl";
import { OrganizationTabKeys } from "~enums/organizationTabKeys";
import { PermissionNames } from "~enums/permissions";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import type { TabRoute } from "../types/TabRoute";

const EntityGroupList = lazy(() => import("~pages/EntityGroups/EntityGroupListPage/EntityGroupListPage.tsx"));
const CompetitionListPage = lazy(() => import("~pages/Competitions/CompetitionListPage/CompetitionListPage.tsx"));
const ConferenceListPage = lazy(() => import("~pages/Conferences/ConferenceListPage/ConferenceListPage.tsx"));
const LeaderCriteriaListPage = lazy(() => import("~pages/LeaderCriteria/LeaderCriteriaListPage/LeaderCriteriaListPage.tsx"));
const LeagueListPage = lazy(() => import("~pages/Leagues/LeagueListPage/LeagueListPage"));
const MatchProfileListPage = lazy(() => import("~pages/MatchProfiles/MatchProfileListPage/MatchProfileListPage.tsx"));
const SiteListPage = lazy(() => import("~pages/Sites/SiteListPage/SiteListPage.tsx"));
const StandingsConfigurationPage = lazy(
  () => import("~pages/StandingsConfigurations/StandingsConfigurationListPage/StandingsConfigurationListPage.tsx"),
);
const EntityListPage = lazy(() => import("~pages/Entities/EntitiesListPage/EntitiesListPage.tsx"));
const VenueListPage = lazy(() => import("~pages/Venues/VenueListPage/VenueListPage.tsx"));
const PersonListPage = lazy(() => import("~pages/Persons/PersonListPage/PersonListPage.tsx"));
const DashboardPage = lazy(() => import("~pages/Dashboard/DashboardPage"));
const ConductListPage = lazy(() => import("~pages/Conducts/ConductListPage/ConductListPage"));
const PermissionUserListPage = lazy(() => import("~pages/Users/PermissionUserListPage/PermissionUserListPage"));

const useGetOrganizationTabRoutes = (): TabRoute<OrganizationTabKeys>[] => {
  const { entityPermissionName } = usePermissionsByCompetitionName();
  const { formatMessage } = useIntl();
  const { uiConfiguration } = useSportConfiguration();
  const { organizationTabs } = uiConfiguration;

  return useMemo(() => {
    const routes: TabRoute<OrganizationTabKeys>[] = [
      {
        key: OrganizationTabKeys.Dashboard,
        path: "dashboard",
        label: formatMessage({ id: "organization.tabs.dashboard" }),
        permissionKeys: [PermissionNames.ORGANIZATION],
        Component: DashboardPage,
      },
      {
        key: OrganizationTabKeys.Leagues,
        path: "leagues",
        label: formatMessage({ id: "organization.tabs.leagues" }),
        permissionKeys: [PermissionNames.LEAGUE],
        Component: LeagueListPage,
      },
      {
        key: OrganizationTabKeys.Competitions,
        path: "competitions",
        label: formatMessage({ id: "organization.tabs.competitions" }),
        permissionKeys: [PermissionNames.COMPETITION],
        Component: CompetitionListPage,
      },
      {
        key: OrganizationTabKeys.Conferences,
        path: "conferences",
        label: formatMessage({ id: "organization.tabs.conferences" }),
        permissionKeys: [PermissionNames.CONFERENCE],
        Component: ConferenceListPage,
      },
      {
        key: OrganizationTabKeys.EntityGroups,
        path: "entity-groups",
        label: formatMessage({ id: "organization.tabs.entityGroups" }),
        permissionKeys: [PermissionNames.ENTITY_GROUP],
        Component: EntityGroupList,
      },
      {
        key: OrganizationTabKeys.Entities,
        path: "entities",
        label: formatMessage({ id: "organization.tabs.entities" }),
        permissionKeys: [entityPermissionName as PermissionNames],
        Component: EntityListPage,
      },
      {
        key: OrganizationTabKeys.Sites,
        path: "sites",
        label: formatMessage({ id: "organization.tabs.sites" }),
        permissionKeys: [PermissionNames.SITE],
        Component: SiteListPage,
      },
      {
        key: OrganizationTabKeys.Venues,
        path: "venues",
        label: formatMessage({ id: "organization.tabs.venues" }),
        permissionKeys: [PermissionNames.VENUE],
        Component: VenueListPage,
      },
      {
        key: OrganizationTabKeys.Persons,
        path: "persons",
        label: formatMessage({ id: "organization.tabs.persons" }),
        permissionKeys: [PermissionNames.PERSON],
        Component: PersonListPage,
      },
      {
        key: OrganizationTabKeys.Permissions,
        path: "permissions",
        label: formatMessage({ id: "organization.tabs.permissions" }),
        permissionKeys: [PermissionNames.PERMISSIONS],
        Component: PermissionUserListPage,
      },
      {
        key: OrganizationTabKeys.StandingsConfigurations,
        path: "standings-configurations",
        label: formatMessage({ id: "organization.tabs.standings.configurations" }),
        permissionKeys: [PermissionNames.STANDING_CONFIGURATION],
        Component: StandingsConfigurationPage,
      },
      {
        key: OrganizationTabKeys.LeadersQualification,
        path: "leaders-qualification",
        label: formatMessage({ id: "organization.tabs.leaders.qualification" }),
        permissionKeys: [PermissionNames.LEADER_QUALIFIER],
        Component: LeaderCriteriaListPage,
      },
      {
        key: OrganizationTabKeys.MatchProfiles,
        path: "match-profiles",
        label: formatMessage({ id: "organization.tabs.match.profiles" }),
        permissionKeys: [PermissionNames.MATCH_PROFILE],
        Component: MatchProfileListPage,
      },
      {
        key: OrganizationTabKeys.Conducts,
        path: "conducts",
        label: formatMessage({ id: "organization.tabs.conduct" }),
        permissionKeys: [PermissionNames.CONDUCT],
        Component: ConductListPage,
      },
    ];

    return routes.filter(({ key }) => organizationTabs[key].enabled);
  }, [entityPermissionName, formatMessage, organizationTabs]);
};

export default useGetOrganizationTabRoutes;
